import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Empty,
  List,
  Popconfirm,
  Row,
  Segmented,
  Skeleton,
  Tag,
  Typography,
} from "antd";
import {
  ApartmentOutlined,
  BankOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  IdcardOutlined,
  MailOutlined,
  MoonOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  SunOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../utils/httpClient";
import { ApprovalSubstitute, Stock, User } from "../utils/types";
import { useThemeMode } from "../context/ThemeContext";
import { useUser } from "../context/UserContext";
import { fetchData, handleRowDelete, hasRoleAccess } from "../utils/constants";
import OutOfOfficeModal from "../components/Modals/OutOfOfficeModal";
import MySignatureCard from "../components/Modals/MySignatureCard";
import "./Profile.scss";

const { Title, Text } = Typography;

type ProfileFieldProps = {
  icon: React.ReactNode;
  label: string;
  value?: React.ReactNode;
};

const displayValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return "Not provided";
  return String(value)
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const ProfileField = ({ icon, label, value }: ProfileFieldProps) => (
  <div className="profile-field">
    <span className="profile-field__icon">{icon}</span>
    <div>
      <Text type="secondary" className="profile-field__label">
        {label}
      </Text>
      <div className="profile-field__value">{value || "Not provided"}</div>
    </div>
  </div>
);

const Profile = () => {
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState<Stock | null>(null);
  const { mode, setMode } = useThemeMode();
  const { roles } = useUser();
  const isManager = hasRoleAccess(roles, ["MANAGER"]);
  const canUseSignature = hasRoleAccess(roles, ["MANAGER", "EMPLOYEE"]);

  const [ooOPeriods, setOoOPeriods] = useState<ApprovalSubstitute[]>([]);
  const [managers, setManagers] = useState<User[]>([]);
  const [isOoOModalOpen, setIsOoOModalOpen] = useState(false);
  const [editingOoOPeriod, setEditingOoOPeriod] =
    useState<ApprovalSubstitute | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await apiClient.get<User>(
          `${process.env.REACT_APP_API_URL}/users/me`,
        );
        setProfile(response.data);
      } catch (requestError) {
        console.error("Failed to load profile:", requestError);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  useEffect(() => {
    const loadLeaveBalance = async () => {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/leave-balance/my-summary`,
        (data: Stock) => setLeaveBalance(data),
      );
    };

    loadLeaveBalance();
  }, []);

  const loadOutOfOfficeData = useCallback(async () => {
    if (!isManager || !profile) return;

    await fetchData(
      `${process.env.REACT_APP_API_URL}/substitutes/mine`,
      (periods: ApprovalSubstitute[]) =>
        setOoOPeriods(
          [...periods].sort((a, b) => (a.startDate < b.startDate ? 1 : -1)),
        ),
    );
    await fetchData(
      `${process.env.REACT_APP_API_URL}/users/displayAllUsers`,
      (allUsers: User[]) =>
        setManagers(
          allUsers.filter(
            (candidate) =>
              candidate.roles?.includes("MANAGER") &&
              candidate.email !== profile?.email,
          ),
        ),
    );
  }, [isManager, profile]);

  useEffect(() => {
    loadOutOfOfficeData();
  }, [loadOutOfOfficeData]);

  const openCreateOoOModal = () => {
    setEditingOoOPeriod(null);
    setIsOoOModalOpen(true);
  };

  const openEditOoOModal = (period: ApprovalSubstitute) => {
    setEditingOoOPeriod(period);
    setIsOoOModalOpen(true);
  };

  if (loading) {
    return (
      <div className="profile-page">
        <Skeleton active avatar paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="profile-page">
        <Alert
          type="error"
          showIcon
          message="We couldn't load your profile"
          description="Please refresh the page or try again later."
        />
      </div>
    );
  }

  const initials = `${profile.firstName?.[0] || ""}${profile.lastName?.[0] || ""}`;
  const fullName = `${profile.firstName || ""} ${profile.lastName || ""}`.trim();
  const isActive = String(profile.status).toUpperCase() === "ACTIVE";

  return (
    <div className="profile-page">
      <section className="profile-hero">
        <Avatar size={88} className="profile-hero__avatar">
          {initials || <UserOutlined />}
        </Avatar>
        <div className="profile-hero__identity">
          <div className="profile-hero__eyebrow">MY PROFILE</div>
          <Title level={2}>{fullName || "User profile"}</Title>
          <Text>{profile.jobTitle || "Employee"}</Text>
          <div className="profile-hero__meta">
            <Tag color={isActive ? "success" : "default"}>
              {displayValue(String(profile.status))}
            </Tag>
            {profile.departmentName && <span>{profile.departmentName}</span>}
            {profile.companyName && <span>{profile.companyName}</span>}
          </div>
        </div>
      </section>

      <Row gutter={[20, 20]}>
        <Col xs={24} xl={16}>
          <div className="profile-sections">
            <Card title="Contact & reporting" className="profile-card">
              <div className="profile-grid">
                <ProfileField
                  icon={<IdcardOutlined />}
                  label="Employee code"
                  value={profile.employeeCode}
                />
                <ProfileField
                  icon={<MailOutlined />}
                  label="Email address"
                  value={profile.email}
                />
                <ProfileField
                  icon={<TeamOutlined />}
                  label="Manager"
                  value={profile.managerFullName}
                />
                <ProfileField
                  icon={<UserOutlined />}
                  label="Timekeeper"
                  value={profile.timekeeperFullName}
                />
              </div>
            </Card>

            {canUseSignature && <MySignatureCard />}

            {isManager && (
              <Card
                title="Out of Office"
                className="profile-card"
                extra={
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={openCreateOoOModal}
                  >
                    Add period
                  </Button>
                }
              >
                {ooOPeriods.length ? (
                  <List
                    dataSource={ooOPeriods}
                    renderItem={(period) => (
                      <List.Item
                        actions={[
                          <Button
                            key="edit"
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => openEditOoOModal(period)}
                          >
                            Edit
                          </Button>,
                          <Popconfirm
                            key="delete"
                            title="Delete this Out of Office period?"
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            onConfirm={() =>
                              handleRowDelete(
                                "Out of Office period",
                                `${process.env.REACT_APP_API_URL}/substitutes`,
                                period,
                                loadOutOfOfficeData,
                              )
                            }
                          >
                            <Button type="text" danger icon={<DeleteOutlined />}>
                              Delete
                            </Button>
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          title={
                            <>
                              {period.substituteUserFullName}{" "}
                              <Tag
                                color={
                                  period.status === "ACTIVE"
                                    ? "success"
                                    : "default"
                                }
                              >
                                {period.status === "ACTIVE"
                                  ? "Active"
                                  : "Inactive"}
                              </Tag>
                            </>
                          }
                          description={`${dayjs(period.startDate).format("DD MMM YYYY")} - ${dayjs(period.endDate).format("DD MMM YYYY")}`}
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty
                    description="No Out of Office periods defined"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </Card>
            )}

            <Card title="Organization & employment" className="profile-card">
              <div className="profile-grid">
                <ProfileField
                  icon={<BankOutlined />}
                  label="Company"
                  value={profile.companyName}
                />
                <ProfileField
                  icon={<ApartmentOutlined />}
                  label="Department"
                  value={profile.departmentName}
                />
                <ProfileField
                  icon={<IdcardOutlined />}
                  label="Employee type"
                  value={displayValue(profile.employeeType)}
                />
                <ProfileField
                  icon={<ClockCircleOutlined />}
                  label="Work schedule"
                  value={profile.workHours ? `${profile.workHours} hours / day` : undefined}
                />
                <ProfileField
                  icon={<SafetyCertificateOutlined />}
                  label="Base contract"
                  value={profile.baseContract ? "Yes" : "No"}
                />
              </div>
            </Card>
          </div>
        </Col>

        <Col xs={24} xl={8}>
          <div className="profile-sections">
            <Card title="Appearance" className="profile-card">
              <Text type="secondary">
                Choose how HolidayApp looks on this device.
              </Text>
              <Segmented
                block
                className="theme-selector"
                value={mode}
                onChange={(value) => setMode(value as "light" | "dark")}
                options={[
                  { label: "Light", value: "light", icon: <SunOutlined /> },
                  { label: "Dark", value: "dark", icon: <MoonOutlined /> },
                ]}
              />
            </Card>

            <Card title="Annual Leave Balance" className="profile-card">
              <div className="leave-entitlement">
                <div>
                  <CalendarOutlined />
                  <strong>
                    {(leaveBalance?.currentYearDays ?? 0) +
                      (leaveBalance?.previousYearDays ?? 0)}
                  </strong>
                  <Text type="secondary">Total available annual leave</Text>
                </div>
                <div>
                  <CalendarOutlined />
                  <strong>{leaveBalance?.takenDays ?? 0}</strong>
                  <Text type="secondary">Taken days</Text>
                </div>
                <div>
                  <CalendarOutlined />
                  <strong>{leaveBalance?.availableDays ?? 0}</strong>
                  <Text type="secondary">Remaining days</Text>
                </div>
              </div>
            </Card>

            <Card title="System roles" className="profile-card">
              <div className="profile-roles">
                {profile.roles?.length ? (
                  profile.roles.map((role) => (
                    <Tag key={role} color="blue">
                      {displayValue(role.replace("ROLE_", ""))}
                    </Tag>
                  ))
                ) : (
                  <Text type="secondary">No roles assigned</Text>
                )}
              </div>
            </Card>
          </div>
        </Col>
      </Row>

      <Text type="secondary" className="profile-readonly-note">
        Your profile information is read-only. Contact an administrator if something needs to be updated.
      </Text>

      {isManager && (
        <OutOfOfficeModal
          isOpen={isOoOModalOpen}
          setIsOpen={setIsOoOModalOpen}
          managers={managers}
          editing={editingOoOPeriod}
          refreshData={loadOutOfOfficeData}
        />
      )}
    </div>
  );
};

export default Profile;
